/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { ApiResponse } from '../types/api';
import { axiosBaseQuery } from './axiosQuery';
import { SurveyActionData } from '../types/surveyAction';
import { StatsUser, SurveyDelegationUser } from '@g17eco/types/user';
import {
  InitiativeSurveyStats,
  QueryStats,
  SurveyStats,
  UserDelegationStats,
  type InitiativeSurveyDeadlineStats,
  type QueryDeadlineStats,
} from '../types/initiativeStats';

interface UserSurveyStats {
  user: StatsUser,
  surveys: SurveyStats[]
}

// Define a service using a base URL and expected endpoints
export const initiativeApi = createApi({
  reducerPath: 'initiativeApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['stats', 'user-stats', 'stats-survey-users'],
  endpoints: (builder) => ({
    getStats: builder.query<InitiativeSurveyStats[], QueryStats>({
      transformResponse: (response: ApiResponse<InitiativeSurveyStats[]>) => response.data,
      providesTags: ['stats'],
      query: ({ initiativeId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats`,
        method: 'get',
        params: params,
      }),
    }),
    getUserSurveyStats: builder.query<UserSurveyStats, QueryStats & { userId: string }>({
      transformResponse: (response: ApiResponse<UserSurveyStats>) => response.data,
      query: ({ initiativeId, userId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats/users/${userId}`,
        method: 'get',
        params: params,
      }),
    }),
    getUserSurveyDetails: builder.query<SurveyActionData, QueryStats & { userId: string, surveyId: string }>({
      transformResponse: (response: ApiResponse<SurveyActionData>) => response.data,
      query: ({ initiativeId, userId, surveyId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats/users/${userId}/surveys/${surveyId}`,
        method: 'get',
        params: params,
      }),
    }),
    getUserStats: builder.query<UserDelegationStats[], QueryDeadlineStats>({
      transformResponse: (response: ApiResponse<UserDelegationStats[]>) => response.data,
      providesTags: ['user-stats'],
      query: ({ initiativeId, ...data }) => ({
        url: `/initiatives/${initiativeId}/stats/users`,
        method: 'post',
        data,
      }),
    }),
    getSurveyUsers: builder.query<SurveyDelegationUser[], { initiativeId: string, surveyId: string }>({
      transformResponse: (response: ApiResponse<SurveyDelegationUser[]>) => response.data,
      providesTags: ['stats-survey-users'],
      query: ({ initiativeId, surveyId }) => ({
        url: `/initiatives/${initiativeId}/stats/surveys/${surveyId}/stakeholders`,
        method: 'get',
      }),
    }),
    getSurveyDeadlineStats: builder.query<InitiativeSurveyDeadlineStats[], QueryDeadlineStats>({
      transformResponse: (response: ApiResponse<InitiativeSurveyDeadlineStats[]>) => response.data,
      providesTags: ['stats'],
      query: ({ initiativeId, ...data }) => ({
        url: `/initiatives/${initiativeId}/stats/surveys`,
        method: 'post',
        data,
      }),
    })
  }),
})

export const {
  useGetStatsQuery,
  useGetUserStatsQuery,
  useGetUserSurveyDetailsQuery,
  useGetUserSurveyStatsQuery,
  /** @deprecated Only being used for admin scope and does not contain workgroups level */
  useGetSurveyUsersQuery,
  useGetSurveyDeadlineStatsQuery,
} = initiativeApi;
